import { useState, useEffect } from 'react';
import { useAutocomplete } from '../hooks/useAutocomplete';
import { useLocation, useNavigate } from 'react-router-dom';
import { sanitizeQuery, sanitizeText, validateFundingTarget, MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH } from '../lib/guardrails';
import Navbar from './navbar';
import Footer from './Footer';
import { FiSearch, FiTrendingUp, FiDollarSign, FiGlobe, FiUsers, FiStar, FiExternalLink, FiInfo, FiBookOpen, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { STARTUP_API_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import { useProgressiveSearch } from '../hooks/useProgressiveSearch';
import RippleWaveLoader from './ui/RippleWaveLoader';

const VC_PROMPTS = [
  'AI/ML seed stage VCs in India',
  'Fintech Series A investors US',
  'Healthcare deep tech VCs Europe',
  'B2B SaaS growth investors',
  'Climate tech impact funds',
];

const VC_LOADING_MESSAGES = [
  'Scanning VC databases worldwide…',
  'Analyzing investment patterns…',
  'Filtering by your criteria…',
  'Enriching with portfolio data…',
  'Almost ready…',
];

interface VC {
  id?: number;
  name: string;
  description?: string;
  website?: string;
  linkedin_url?: string;
  headquarters?: string;
  aum?: string;
  investment_focus?: string[];
  stage_focus?: string;
  check_size_min?: string;
  check_size_max?: string;
  geographic_focus?: string[];
  portfolio_count?: number;
  founded_year?: string;
  key_partners?: { name: string; focus_areas?: string }[];
  notable_portfolio?: string[];
  recent_investments?: { company: string; round: string; amount: string }[];
  source?: string[];
}

interface FundingTrend {
  period_analyzed?: string;
  deal_summary?: {
    total_deals?: string;
    total_amount?: string;
    average_deal_size?: string;
    trend_vs_previous?: string;
  };
  hot_sectors?: { sector: string; deals: string; amount: string }[];
  key_insights?: string[];
  sources?: string[];
  articles?: { title?: string; url: string; summary?: string }[];
}

interface MatchedInvestor {
  name: string;
  match_score: number;
  sector_fit: number;
  stage_fit: number;
  geographic_fit: number;
  thesis_fit: number;
  founded_year?: string;
  aum?: string;
  check_size?: string;
  history?: string;
  comparisons?: string;
  rationale: string;
  relevant_portfolio?: string[];
  recent_investments?: string[];
  contact_approach?: string;
  key_partners?: string[];
  website?: string;
  source?: string[];
  articles?: { title?: string; url: string; summary?: string }[];
}

interface MatchResult {
  startup_summary?: string;
  matching_criteria?: {
    sector?: string;
    stage?: string;
    geography?: string;
    funding_target?: string;
  };
  matched_investors?: MatchedInvestor[];
  summary?: string;
}

const VCIntelligencePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'search' | 'trends' | 'match' | 'saved'>('search');
  const [savedVCs, setSavedVCs] = useState<VC[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const acVC = useAutocomplete(searchQuery);
  const [sector, setSector] = useState('');
  const [stage, setStage] = useState('');
  const [geography, setGeography] = useState('India');
  const [vcResults, setVcResults] = useState<VC[]>([]);
  const [visibleCount, setVisibleCount] = useState(4);
  const [trends, setTrends] = useState<FundingTrend | null>(null);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [savedNames, setSavedNames] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const webSearch = useProgressiveSearch();
  const [showWebSources, setShowWebSources] = useState(false);

  // Pre-fill search bar and auto-search when navigated from chat
  useEffect(() => {
    const q = location.state?.search_query;
    if (!q) return;
    const clean = sanitizeQuery(q);
    if (!clean) return;
    window.history.replaceState({}, document.title);
    handleVCSearch(clean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Investor Match form state
  const [startupName, setStartupName] = useState('');
  const [startupSector, setStartupSector] = useState('');
  const [startupStage, setStartupStage] = useState('');
  const [fundingTarget, setFundingTarget] = useState('');
  const [startupGeo, setStartupGeo] = useState('India');
  const [startupDescription, setStartupDescription] = useState('');

  const sectors = [
    'AI/ML', 'Fintech', 'Healthcare', 'SaaS', 'Consumer',
    'Enterprise', 'Crypto/Web3', 'Climate Tech', 'Deep Tech',
    'Cybersecurity', 'EdTech', 'PropTech', 'AgriTech', 'Logistics'
  ];

  const stages = ['Seed', 'Series A', 'Series B', 'Series C', 'Growth', 'Multi-stage'];

  const regions = ['US', 'Europe', 'Asia', 'India', 'LATAM', 'Global'];

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const fetchSavedVCs = async () => {
    setSavedLoading(true);
    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/vc/db?limit=100`, {
        headers: { 'Authorization': localStorage.getItem('authToken') || '' }
      });
      const data = await response.json();
      if (data.status === 'success') setSavedVCs(data.data || []);
    } catch (error) {
      console.error('Error fetching saved VCs:', error);
    } finally {
      setSavedLoading(false);
    }
  };

  const handleVCSearch = async (overrideQuery?: string) => {
    const safeQuery = sanitizeQuery(overrideQuery ?? searchQuery);
    if (overrideQuery !== undefined) setSearchQuery(overrideQuery);
    if (!safeQuery && !sector && !stage && !geography) return;

    setLoading(true);
    setHasSearched(true);
    setMessage(null);
    setVisibleCount(4);
    setShowWebSources(true);

    // Build a web search query and kick off progressive search in parallel
    const webParts = [safeQuery, sector, stage, geography].filter(Boolean);
    const webQuery = webParts.length ? `${webParts.join(' ')} venture capital` : 'venture capital firms';
    webSearch.search(webQuery, geography === 'India' ? 'IN' : geography || 'IN', 20, 5);

    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/vc/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({
          search_query: safeQuery,
          sector: sector || undefined,
          stage: stage || undefined,
          geography: geography || undefined
        })
      });

      if (!response.ok) {
        showMessage(`Search failed (HTTP ${response.status})`, 'error');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.status === 'success') {
        let results: VC[] = data.data || [];
        // Filter client-side by sector if set
        if (sector && results.length > 0) {
          const sectorLower = sector.toLowerCase();
          const sectorAliases: Record<string, string[]> = {
            'ai/ml': ['ai', 'ml', 'artificial intelligence', 'machine learning', 'deep learning', 'nlp'],
            'fintech': ['fintech', 'financial technology', 'payments', 'insurtech', 'wealthtech'],
            'healthcare': ['healthcare', 'health tech', 'healthtech', 'medtech', 'digital health', 'biotech'],
            'saas': ['saas', 'software as a service', 'cloud software', 'b2b software'],
            'consumer': ['consumer', 'd2c', 'e-commerce', 'ecommerce', 'retail tech'],
            'enterprise': ['enterprise', 'b2b', 'enterprise software'],
            'crypto/web3': ['crypto', 'web3', 'blockchain', 'defi'],
            'climate tech': ['climate', 'cleantech', 'clean energy', 'sustainability', 'green tech'],
            'deep tech': ['deep tech', 'deeptech', 'hardware', 'robotics', 'semiconductors'],
            'cybersecurity': ['cybersecurity', 'security', 'infosec', 'cyber'],
            'edtech': ['edtech', 'education tech', 'e-learning'],
            'proptech': ['proptech', 'real estate tech', 'property tech'],
            'agritech': ['agritech', 'agri tech', 'agriculture tech', 'agtech'],
            'logistics': ['logistics', 'supply chain', 'freight tech'],
          };
          const aliases = sectorAliases[sectorLower] || [sectorLower];
          const sectorFiltered = results.filter(vc => {
            const focus = (vc.investment_focus || []).join(' ').toLowerCase();
            if (!focus) return true;
            return aliases.some(a => focus.includes(a));
          });
          results = sectorFiltered.length > 0 ? sectorFiltered : results;
        }
        // Filter client-side by stage if set (belt-and-suspenders after backend filter)
        if (stage && results.length > 0) {
          const stageLower = stage.toLowerCase();
          const stageFiltered = results.filter(vc => {
            const sf = (vc.stage_focus || '').toLowerCase();
            if (!sf) return true;
            if (sf.includes('multi') || sf.includes('all stage')) return true;
            if (sf.includes(stageLower)) return true;
            return false;
          });
          results = stageFiltered.length > 0 ? stageFiltered : results;
        }
        // Filter client-side by geography/headquarters if set
        if (geography && results.length > 0) {
          const geoLower = geography.toLowerCase();
          const filtered = results.filter(vc =>
            (vc.headquarters || '').toLowerCase().includes(geoLower) ||
            (vc.geographic_focus || []).some(g => g.toLowerCase().includes(geoLower))
          );
          results = filtered.length > 0 ? filtered : results;
        }
        // Sort A-Z by name
        results = [...results].sort((a, b) => a.name.localeCompare(b.name));
        setVcResults(results);
        if (results.length === 0) {
          showMessage('No VCs found. Try adjusting your search.', 'error');
        }
      } else {
        showMessage(data.message || 'Search failed', 'error');
      }
    } catch (error) {
      console.error('Error searching VCs:', error);
      showMessage('Could not connect to server. Make sure the backend is running.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVC = async (vc: VC, index: number) => {
    setSavingIndex(index);
    setMessage(null);
    try {
      const savePayload = {
        name: vc.name,
        description: vc.description,
        website: vc.website,
        linkedin_url: vc.linkedin_url,
        headquarters: vc.headquarters,
        aum: vc.aum,
        investment_focus: vc.investment_focus || [],
        stage_focus: vc.stage_focus,
        check_size_min: vc.check_size_min,
        check_size_max: vc.check_size_max,
        geographic_focus: vc.geographic_focus || [],
        portfolio_count: vc.portfolio_count,
        founded_year: vc.founded_year,
        source: Array.isArray(vc.source) ? vc.source.join(', ') : vc.source,
      };

      const response = await fetch(`${STARTUP_API_URL}/api/v1/vc/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify(savePayload)
      });

      const data = await response.json();
      if (data.status === 'success') {
        showMessage(`${vc.name} saved successfully!`, 'success');
        setSavedNames(prev => new Set(prev).add(vc.name));
      } else {
        showMessage(data.message || 'Failed to save VC', 'error');
      }
    } catch (error) {
      console.error('Error saving VC:', error);
      showMessage('Could not connect to server. Make sure the backend is running.', 'error');
    } finally {
      setSavingIndex(null);
    }
  };

  const handleGetTrends = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const params = new URLSearchParams();
      if (sector) params.set('sector', sector);

      const response = await fetch(
        `${STARTUP_API_URL}/api/v1/vc/trends?${params.toString()}`,
        {
          headers: {
            'Authorization': localStorage.getItem('authToken') || ''
          }
        }
      );

      if (!response.ok) {
        showMessage(`Failed to get trends (HTTP ${response.status})`, 'error');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.status === 'success') {
        setTrends(data.data);
        if (!data.data) {
          showMessage('No trend data available for this sector.', 'error');
        }
      } else {
        showMessage(data.message || 'Failed to get trends', 'error');
      }
    } catch (error) {
      console.error('Error getting trends:', error);
      showMessage('Could not connect to server. Make sure the backend is running.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInvestorMatch = async () => {
    const safeName = sanitizeText(startupName, MAX_NAME_LENGTH);
    const safeDescription = sanitizeText(startupDescription, MAX_DESCRIPTION_LENGTH);
    const fundingCheck = validateFundingTarget(fundingTarget);

    if (!safeName || !startupSector) {
      showMessage('Please provide at least the startup name and sector.', 'error');
      return;
    }
    if (!fundingCheck.valid) {
      showMessage(fundingCheck.error!, 'error');
      return;
    }

    setLoading(true);
    setMessage(null);
    setMatchResult(null);

    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/vc/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({
          startup_name: safeName,
          sector: startupSector,
          stage: startupStage || undefined,
          funding_target: fundingTarget || undefined,
          geography: startupGeo || undefined,
          description: safeDescription || undefined
        })
      });

      if (!response.ok) {
        showMessage(`Matching failed (HTTP ${response.status})`, 'error');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.status === 'success') {
        setMatchResult(data.data || {});
        const investorCount = data.data?.matched_investors?.length || 0;
        if (investorCount > 0) {
          showMessage(`Found ${investorCount} matched investors!`, 'success');
        } else {
          showMessage('No investor matches found. Try adjusting your profile.', 'error');
        }
      } else {
        showMessage(data.message || 'Matching failed', 'error');
      }
    } catch (error) {
      console.error('Error matching investors:', error);
      showMessage('Could not connect to server. Make sure the backend is running.', 'error');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-4 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center relative">
          <button
            onClick={() => navigate("/")}
            className="absolute left-0 top-0 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back to Chat
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            VC <span className="text-[#FF541F]">Intelligence</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Discover VCs, analyze funding trends, and find the right investors
          </p>

          {/* Tab Navigation */}
          <div className="flex justify-center gap-4 mb-8">
            {[
              { id: 'search', label: 'Find VCs', icon: FiSearch },
              { id: 'trends', label: 'Funding Trends', icon: FiTrendingUp },
              { id: 'match', label: 'Investor Match', icon: FiUsers },
              { id: 'saved', label: 'Saved VCs', icon: FiStar }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id as any); if (tab.id === 'saved') fetchSavedVCs(); }}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#FF541F] text-white'
                    : 'bg-[#1A1A1A] text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="px-4 md:px-12 lg:px-24 pb-16">
        {/* Message Banner */}
        {message && (
          <div className={`max-w-6xl mx-auto mb-6 p-4 rounded-lg flex items-center justify-between ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-4 hover:opacity-70">&times;</button>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="max-w-6xl mx-auto">
            {/* Quick Prompts */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Quick prompts:</p>
              <div className="flex flex-wrap gap-2">
                {VC_PROMPTS.map(p => (
                  <button
                    key={p}
                    onClick={() => setSearchQuery(p)}
                    className="px-3 py-1.5 text-xs rounded-full bg-white/5 border border-white/10 text-gray-400 hover:border-[#FF541F]/50 hover:text-[#FF541F] transition-all"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Controls */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); acVC.setShowSuggestions(true); }}
                  onKeyDown={e => acVC.handleKeyDown(e, s => { setSearchQuery(s); acVC.clear(); handleVCSearch(s); }, () => handleVCSearch())}
                  onBlur={() => setTimeout(() => acVC.clear(), 150)}
                  placeholder="e.g., AI VCs investing in seed stage India…"
                  className="w-full px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF541F] text-[15px]"
                />
                {acVC.showSuggestions && acVC.suggestions.length > 0 && (
                  <ul className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ background: "#1a1a1a" }}>
                    {acVC.suggestions.map((s, i) => (
                      <li key={s} onMouseDown={() => { setSearchQuery(s); acVC.clear(); handleVCSearch(s); }}
                        className="px-4 py-2.5 text-sm cursor-pointer transition-colors"
                        style={{ background: i === acVC.activeSuggestion ? "rgba(255,84,31,0.15)" : "transparent", color: i === acVC.activeSuggestion ? "#FF541F" : "#d1d5db" }}>
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Sectors</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Stages</option>
                {stages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={geography}
                onChange={(e) => setGeography(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Regions</option>
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button
                onClick={() => handleVCSearch()}
                disabled={loading}
                className="px-6 py-3 bg-[#FF541F] text-white rounded-lg hover:bg-[#FF541F]/80 transition-all disabled:opacity-50"
              >
                {loading ? 'Searching...' : 'Search VCs'}
              </button>
            </div>

            {/* VC Results */}
            {loading && (
              <div className="flex justify-center py-16">
                <RippleWaveLoader messages={VC_LOADING_MESSAGES} />
              </div>
            )}

            {!loading && hasSearched && vcResults.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No VCs found. Try adjusting your search.</p>
              </div>
            )}

            {!loading && vcResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {vcResults.slice(0, visibleCount).map((vc, index) => (
                  <motion.div
                    key={`${vc.name}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 hover:border-[#FF541F]/50 transition-all"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-white">{vc.name}</h3>
                        {vc.headquarters && (
                          <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                            <FiGlobe size={14} /> {vc.headquarters}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {vc.website && (
                          <a
                            href={vc.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                          >
                            <FiExternalLink size={14} className="text-gray-400" />
                          </a>
                        )}
                        <button
                          onClick={() => !savedNames.has(vc.name) && handleSaveVC(vc, index)}
                          disabled={savingIndex === index}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-sm disabled:cursor-wait ${
                            savedNames.has(vc.name)
                              ? 'bg-green-500/20 text-green-400 cursor-default'
                              : 'bg-[#FF541F]/20 text-[#FF541F] hover:bg-[#FF541F]/30 disabled:opacity-50'
                          }`}
                        >
                          {savingIndex === index ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-[#FF541F]"></div>
                              Saving...
                            </>
                          ) : savedNames.has(vc.name) ? (
                            <>
                              <FiStar size={14} className="fill-green-400" />
                              Saved
                            </>
                          ) : (
                            <>
                              <FiStar size={14} />
                              Save
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {vc.description || 'No description available'}
                    </p>

                    {/* Investment Focus */}
                    {vc.investment_focus && vc.investment_focus.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {vc.investment_focus.slice(0, 4).map((focus, i) => (
                          <span key={i} className="px-2 py-1 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">
                            {focus}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {vc.stage_focus && (
                        <div>
                          <span className="text-gray-500">Stage Focus:</span>
                          <span className="text-white ml-2">{vc.stage_focus}</span>
                        </div>
                      )}
                      {(vc.check_size_min || vc.check_size_max) && (
                        <div>
                          <span className="text-gray-500">Check Size:</span>
                          <span className="text-white ml-2">
                            {vc.check_size_min} - {vc.check_size_max}
                          </span>
                        </div>
                      )}
                      {vc.aum && (
                        <div>
                          <span className="text-gray-500">AUM:</span>
                          <span className="text-white ml-2">{vc.aum}</span>
                        </div>
                      )}
                      {vc.portfolio_count && (
                        <div>
                          <span className="text-gray-500">Portfolio:</span>
                          <span className="text-white ml-2">{vc.portfolio_count} companies</span>
                        </div>
                      )}
                    </div>

                    {/* Notable Portfolio */}
                    {vc.notable_portfolio && vc.notable_portfolio.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-gray-500 text-xs mb-2">Notable Portfolio:</p>
                        <div className="flex flex-wrap gap-1">
                          {vc.notable_portfolio.slice(0, 5).map((company, i) => (
                            <span key={i} className="px-2 py-1 text-xs bg-white/5 text-gray-400 rounded">
                              {company}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}

            {/* Load More */}
            {!loading && vcResults.length > visibleCount && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setVisibleCount(v => v + 4)}
                  className="px-8 py-3 bg-[#1A1A1A] border border-white/10 hover:border-[#FF541F]/50 text-gray-300 hover:text-white rounded-lg transition-all text-sm"
                >
                  Load More ({vcResults.length - visibleCount} remaining)
                </button>
              </div>
            )}

            {/* Progressive Web Sources */}
            {showWebSources && (webSearch.results.length > 0 || webSearch.phase === 'serper' || webSearch.phase === 'enriched') && (
              <div className="mt-10">
                <button
                  onClick={() => setShowWebSources(v => !v)}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
                >
                  {showWebSources ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  Web Sources
                  {webSearch.phase === 'serper' || webSearch.phase === 'enriched' ? (
                    <span className="ml-2 flex items-center gap-1 text-xs text-[#FF541F]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF541F] animate-pulse" />
                      {webSearch.phase === 'serper' ? 'Loading more sources...' : 'Enriching results...'}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-600">
                      {webSearch.results.length} results · {Object.entries(webSearch.sourceCount).map(([k, v]) => `${k}: ${v}`).join(', ')}
                    </span>
                  )}
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {webSearch.results.slice(0, 12).map((r, i) => (
                    <a
                      key={i}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 bg-[#0F0F0F] border border-white/5 hover:border-white/20 rounded-lg transition-all group"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm text-white font-medium line-clamp-2 group-hover:text-[#FF541F] transition-colors">
                          {r.title || r.url}
                        </p>
                        <FiExternalLink size={12} className="text-gray-600 flex-shrink-0 mt-0.5" />
                      </div>
                      {r.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{r.description}</p>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          r.source === 'brave' ? 'bg-orange-500/20 text-orange-400' :
                          r.source === 'tavily' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {r.source || 'serper'}
                        </span>
                        {r.url && (
                          <span className="text-xs text-gray-600 truncate">
                            {(() => { try { return new URL(r.url!).hostname.replace('www.', ''); } catch { return r.url; } })()}
                          </span>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-4 mb-8">
              <input
                type="text"
                list="trend-sectors-list"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGetTrends()}
                placeholder="Type or select a sector (e.g. AI/ML, Fintech, Healthcare)..."
                className="flex-1 px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F]"
              />
              <datalist id="trend-sectors-list">
                {sectors.map(s => <option key={s} value={s} />)}
              </datalist>
              <button
                onClick={handleGetTrends}
                disabled={loading}
                className="px-6 py-3 bg-[#FF541F] text-white rounded-lg hover:bg-[#FF541F]/80 transition-all disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? 'Analyzing...' : 'Get Trends'}
              </button>
            </div>

            {loading && (
              <div className="flex justify-center py-16">
                <RippleWaveLoader message="Fetching latest funding trends and deal data…" />
              </div>
            )}

            {!loading && trends && (
              <div className="space-y-6">
                {/* Summary Cards */}
                {trends.deal_summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
                      <FiDollarSign className="mx-auto text-[#FF541F] mb-2" size={24} />
                      <p className="text-base font-semibold text-white">{trends.deal_summary.total_amount || 'N/A'}</p>
                      <p className="text-gray-500 text-sm">Total Invested</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
                      <FiTrendingUp className="mx-auto text-green-500 mb-2" size={24} />
                      <p className="text-base font-semibold text-white">{trends.deal_summary.total_deals || 'N/A'}</p>
                      <p className="text-gray-500 text-sm">Total Deals</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
                      <p className="text-base font-semibold text-white">{trends.deal_summary.average_deal_size || 'N/A'}</p>
                      <p className="text-gray-500 text-sm">Avg Deal Size</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
                      <p className="text-base font-semibold text-white">{trends.deal_summary.trend_vs_previous || 'N/A'}</p>
                      <p className="text-gray-500 text-sm">vs Previous</p>
                    </div>
                  </div>
                )}

                {/* Hot Sectors */}
                {trends.hot_sectors && trends.hot_sectors.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Hot Sectors</h3>
                    <div className="space-y-3">
                      {trends.hot_sectors.map((sector, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-white">{sector.sector}</span>
                          <div className="flex gap-4 text-sm">
                            <span className="text-gray-400">{sector.deals} deals</span>
                            <span className="text-[#FF541F]">{sector.amount}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Insights */}
                {trends.key_insights && trends.key_insights.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Key Insights</h3>
                    <ul className="space-y-2">
                      {trends.key_insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-400">
                          <span className="text-[#FF541F] mt-1">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Articles */}
                {trends.articles && trends.articles.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <FiBookOpen className="text-[#FF541F]" size={20} />
                      Related Articles
                    </h3>
                    <div className="space-y-3">
                      {trends.articles.map((article, i) => {
                        let domain = article.url;
                        try { domain = new URL(article.url).hostname.replace('www.', ''); } catch {}
                        return (
                          <a
                            key={i}
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 p-3 rounded-lg bg-black/30 hover:bg-black/50 border border-white/5 hover:border-[#FF541F]/30 transition-all group"
                          >
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                              alt={domain}
                              className="w-5 h-5 mt-0.5 flex-shrink-0 opacity-70"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-white group-hover:text-[#FF541F] transition-colors line-clamp-1">
                                {article.title || domain}
                              </p>
                              {article.summary && (
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{article.summary}</p>
                              )}
                              <p className="text-xs text-gray-600 mt-0.5">{domain}</p>
                            </div>
                            <FiExternalLink size={12} className="text-gray-600 group-hover:text-[#FF541F] flex-shrink-0 mt-1" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sources */}
                {trends.sources && trends.sources.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6">
                    <h3 className="text-base font-medium text-white mb-3 flex items-center gap-2">
                      <FiExternalLink size={16} className="text-gray-400" />
                      Sources
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {trends.sources.map((src, i) => {
                        let domain = src;
                        try { domain = new URL(src).hostname.replace('www.', ''); } catch {}
                        return (
                          <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs text-gray-400 hover:text-white transition-colors border border-white/10"
                          >
                            <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" className="w-3.5 h-3.5" />
                            {domain}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'match' && (
          <div className="max-w-4xl mx-auto">
            {/* Startup Profile Form */}
            {!matchResult && !loading && (
              <div className="bg-[#1A1A1A] rounded-xl p-8 border border-white/10">
                <h3 className="text-xl font-semibold text-white mb-6">Enter Your Startup Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Startup Name *</label>
                    <input
                      type="text"
                      value={startupName}
                      onChange={(e) => setStartupName(e.target.value)}
                      placeholder="e.g., Acme AI"
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F]"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Sector *</label>
                    <select
                      value={startupSector}
                      onChange={(e) => setStartupSector(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                    >
                      <option value="">Select Sector</option>
                      {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Stage</label>
                    <select
                      value={startupStage}
                      onChange={(e) => setStartupStage(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                    >
                      <option value="">Select Stage</option>
                      {stages.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Funding Target</label>
                    <input
                      type="text"
                      value={fundingTarget}
                      onChange={(e) => setFundingTarget(e.target.value)}
                      placeholder="e.g., $5M"
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F]"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Geography</label>
                    <select
                      value={startupGeo}
                      onChange={(e) => setStartupGeo(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                    >
                      <option value="">Select Region</option>
                      {regions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Description</label>
                    <input
                      type="text"
                      value={startupDescription}
                      onChange={(e) => setStartupDescription(e.target.value)}
                      placeholder="Brief description of your startup"
                      className="w-full px-4 py-3 rounded-lg bg-black border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F]"
                    />
                  </div>
                </div>
                <button
                  onClick={handleInvestorMatch}
                  className="w-full px-6 py-3 bg-[#FF541F] text-white rounded-lg hover:bg-[#FF541F]/80 transition-all font-medium"
                >
                  Find Matching Investors
                </button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-16">
                <RippleWaveLoader messages={[
                  'Analyzing investor landscape…',
                  'Matching your startup profile to VCs…',
                  'Scoring sector and stage fit…',
                  'Compiling top matches…',
                ]} />
              </div>
            )}

            {/* Match Results */}
            {!loading && matchResult && (
              <div className="space-y-6">
                {/* Summary */}
                {matchResult.summary && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-2">Matching Summary</h3>
                    <p className="text-gray-400">{matchResult.summary}</p>
                    {matchResult.matching_criteria && (
                      <div className="flex flex-wrap gap-3 mt-4">
                        {matchResult.matching_criteria.sector && (
                          <span className="px-3 py-1 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded-full">
                            {matchResult.matching_criteria.sector}
                          </span>
                        )}
                        {matchResult.matching_criteria.stage && (
                          <span className="px-3 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                            {matchResult.matching_criteria.stage}
                          </span>
                        )}
                        {matchResult.matching_criteria.geography && (
                          <span className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded-full">
                            {matchResult.matching_criteria.geography}
                          </span>
                        )}
                        {matchResult.matching_criteria.funding_target && (
                          <span className="px-3 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-full">
                            {matchResult.matching_criteria.funding_target}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Back button */}
                <button
                  onClick={() => setMatchResult(null)}
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  &larr; Back to form
                </button>

                {/* Matched Investors */}
                {matchResult.matched_investors && matchResult.matched_investors.length > 0 && (
                  <div className="space-y-4">
                    {matchResult.matched_investors.map((investor, index) => (
                      <motion.div
                        key={`${investor.name}-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 hover:border-[#FF541F]/30 transition-all"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-3">
                              <h4 className="text-lg font-semibold text-white">{investor.name}</h4>
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                investor.match_score >= 80 ? 'bg-green-500/20 text-green-400' :
                                investor.match_score >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {investor.match_score}% match
                              </span>
                            </div>
                          </div>
                          {investor.website && (
                            <a href={investor.website} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                              <FiExternalLink size={14} className="text-gray-400" />
                            </a>
                          )}
                        </div>

                        <p className="text-gray-400 text-sm mb-4">{investor.rationale}</p>

                        {/* Firm Meta: Founded, AUM, Check Size */}
                        {(investor.founded_year || investor.aum || investor.check_size) && (
                          <div className="flex flex-wrap gap-3 mb-4">
                            {investor.founded_year && (
                              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                                <span className="text-gray-500 text-xs">Founded</span>
                                <span className="text-white text-xs font-medium">{investor.founded_year}</span>
                              </div>
                            )}
                            {investor.aum && (
                              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                                <FiDollarSign size={12} className="text-[#FF541F]" />
                                <span className="text-gray-500 text-xs">AUM</span>
                                <span className="text-white text-xs font-medium">{investor.aum}</span>
                              </div>
                            )}
                            {investor.check_size && (
                              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                                <span className="text-gray-500 text-xs">Check</span>
                                <span className="text-white text-xs font-medium">{investor.check_size}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* History */}
                        {investor.history && (
                          <div className="mb-4 p-3 rounded-lg bg-black/30 border border-white/5">
                            <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">History</p>
                            <p className="text-sm text-gray-400">{investor.history}</p>
                          </div>
                        )}

                        {/* Fit Scores */}
                        <div className="grid grid-cols-4 gap-3 mb-1">
                          {[
                            { label: 'Sector', score: investor.sector_fit, tip: "How well the VC's sector focus aligns with your startup's industry" },
                            { label: 'Stage', score: investor.stage_fit, tip: "How well the VC's preferred investment stage matches your current funding stage" },
                            { label: 'Geo', score: investor.geographic_fit, tip: "Geographic overlap — whether the VC actively invests in your region" },
                            { label: 'Thesis', score: investor.thesis_fit, tip: "Alignment with the VC's investment thesis and strategic priorities" },
                          ].map(fit => (
                            <div key={fit.label} className="text-center group relative">
                              <div className={`text-lg font-semibold ${
                                (fit.score ?? 0) >= 8 ? 'text-green-400' :
                                (fit.score ?? 0) >= 6 ? 'text-yellow-400' :
                                (fit.score ?? 0) >= 4 ? 'text-orange-400' : 'text-gray-400'
                              }`}>{fit.score ?? '—'}<span className="text-xs text-gray-500">/10</span></div>
                              <div className="flex items-center justify-center gap-0.5">
                                <p className="text-xs text-gray-500">{fit.label}</p>
                                <FiInfo size={10} className="text-gray-600 cursor-help" />
                              </div>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 bg-[#2A2A2A] border border-white/10 rounded-lg p-2 text-xs text-gray-300 hidden group-hover:block z-50 shadow-xl">
                                {fit.tip}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Score legend */}
                        <div className="flex items-center gap-3 mb-4 text-xs text-gray-600">
                          <FiInfo size={11} className="flex-shrink-0" />
                          <span>Scores 1–10: <span className="text-green-500">8–10</span> strong · <span className="text-yellow-500">6–7</span> good · <span className="text-orange-500">4–5</span> partial · <span className="text-gray-500">1–3</span> weak — hover each score for details</span>
                        </div>

                        {/* Portfolio & Partners */}
                        <div className="flex flex-wrap gap-4 text-sm">
                          {investor.relevant_portfolio && investor.relevant_portfolio.length > 0 && (
                            <div>
                              <span className="text-gray-500">Portfolio: </span>
                              <span className="text-gray-300">{investor.relevant_portfolio.slice(0, 3).join(', ')}</span>
                            </div>
                          )}
                          {investor.key_partners && investor.key_partners.length > 0 && (
                            <div>
                              <span className="text-gray-500">Partners: </span>
                              <span className="text-gray-300">{investor.key_partners.slice(0, 2).join(', ')}</span>
                            </div>
                          )}
                        </div>

                        {investor.contact_approach && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <span className="text-gray-500 text-xs">Approach: </span>
                            <span className="text-gray-400 text-xs">{investor.contact_approach}</span>
                          </div>
                        )}

                        {/* Comparisons */}
                        {investor.comparisons && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">vs. Peers</p>
                            <p className="text-xs text-gray-400 italic">{investor.comparisons}</p>
                          </div>
                        )}

                        {/* Articles */}
                        {investor.articles && investor.articles.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                              <FiBookOpen size={11} /> Related Articles
                            </p>
                            <div className="space-y-1.5">
                              {investor.articles.slice(0, 3).map((art, ai) => {
                                let domain = art.url;
                                try { domain = new URL(art.url).hostname.replace('www.', ''); } catch {}
                                return (
                                  <a key={ai} href={art.url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-xs text-gray-400 hover:text-[#FF541F] transition-colors group"
                                  >
                                    <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="line-clamp-1">{art.title || domain}</span>
                                    <FiExternalLink size={10} className="flex-shrink-0 opacity-0 group-hover:opacity-100" />
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Sources */}
                        {investor.source && investor.source.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-gray-500 mb-2">Sources</p>
                            <div className="flex flex-wrap gap-1.5">
                              {investor.source.slice(0, 4).map((src, si) => {
                                let domain = src;
                                try { domain = new URL(src).hostname.replace('www.', ''); } catch {}
                                return (
                                  <a key={si} href={src} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 text-xs text-gray-500 hover:text-gray-300 border border-white/10 transition-colors"
                                  >
                                    <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" className="w-3 h-3" />
                                    {domain}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Saved VCs Tab */}
        {activeTab === 'saved' && (
          <div>
            {savedLoading && (
              <div className="flex justify-center py-16">
                <RippleWaveLoader message="Loading your saved VCs…" />
              </div>
            )}
            {!savedLoading && savedVCs.length === 0 && (
              <div className="text-center py-12 bg-[#1A1A1A] rounded-xl border border-white/10">
                <FiStar size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 mb-2">No saved VCs yet</p>
                <p className="text-gray-500 text-sm">Search for VCs and click Save to add them here</p>
              </div>
            )}
            {!savedLoading && savedVCs.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {savedVCs.map((vc, index) => (
                  <motion.div
                    key={vc.id ?? index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 hover:border-[#FF541F]/30 transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-white">{vc.name}</h3>
                      {vc.website && (
                        <a href={vc.website} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors">
                          <FiExternalLink size={14} className="text-gray-400" />
                        </a>
                      )}
                    </div>
                    {vc.headquarters && <p className="text-gray-500 text-xs mb-2">{vc.headquarters}</p>}
                    {vc.description && <p className="text-gray-400 text-sm mb-3 line-clamp-2">{vc.description}</p>}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(vc.investment_focus || []).slice(0, 3).map((f, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">{f}</span>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      {vc.stage_focus && <span>Stage: <span className="text-gray-300">{vc.stage_focus}</span></span>}
                      {vc.aum && <span>AUM: <span className="text-gray-300">{vc.aum}</span></span>}
                      {vc.check_size_min && <span>Min: <span className="text-gray-300">{vc.check_size_min}</span></span>}
                      {vc.portfolio_count && <span>Portfolio: <span className="text-gray-300">{vc.portfolio_count} cos</span></span>}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default VCIntelligencePage;
